import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { PaddingControl, BorderRadiusControl, StyleControl, SkinSelector } from '../CommonProperties';
import { usePropertyPanel } from '../PropertyPanelContext';

const ParallaxProperties = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  const { devicePropertyTab, getDevicePropertyValue } = usePropertyPanel();
  
  // 컴포넌트 속성 변경 핸들러 - 컴포넌트의 최상위 속성에 직접 저장
  const handleComponentPropsChange = (property, value) => {
    // 최상위 속성으로 업데이트
    const updatedComponent = JSON.parse(JSON.stringify(selectedComponent));
    
    // 디바이스 속성 탭이 선택된 경우, 해당 디바이스에 대한 속성만 변경
    if (devicePropertyTab) {
      // 디바이스별 객체 초기화
      if (!updatedComponent.deviceProperty) {
        updatedComponent.deviceProperty = {
          pc: {},
          mobile: {}
        };
        
        // 기존 속성을 PC/모바일에 복사
        Object.keys(updatedComponent).forEach(key => {
          if (key !== 'deviceProperty' && !['id', 'type', 'content', 'style'].includes(key)) {
            updatedComponent.deviceProperty.pc[key] = updatedComponent[key];
            updatedComponent.deviceProperty.mobile[key] = updatedComponent[key];
          }
        });
      }
      
      // 선택된 디바이스 탭에 속성 업데이트
      if (!updatedComponent.deviceProperty[devicePropertyTab]) {
        updatedComponent.deviceProperty[devicePropertyTab] = {};
      }
      
      updatedComponent.deviceProperty[devicePropertyTab][property] = value;
      
      // PC 모드일 때만 최상위 속성에도 적용
      if (devicePropertyTab === 'pc') {
        updatedComponent[property] = value;
      }
    } else {
      // 최상위 속성에 직접 업데이트
      updatedComponent[property] = value;
      
      // 디바이스별 속성도 업데이트 (있는 경우)
      if (updatedComponent.deviceProperty) {
        if (updatedComponent.deviceProperty.pc) {
          updatedComponent.deviceProperty.pc[property] = value;
        }
        if (updatedComponent.deviceProperty.mobile) {
          updatedComponent.deviceProperty.mobile[property] = value;
        }
      }
    }
    
    // 컴포넌트 전체 상태 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: updatedComponent
    }));
    
    // 전역 이벤트 발생 - 현재 속성값이 변경되었음을 알림
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('propertyChanged', {
        detail: {
          deviceType: devicePropertyTab || 'all',
          componentId: selectedComponent.id,
          property,
          value
        }
      }));
    }
  };

  // Parallax 속성 가져오기
  const getValue = (property, defaultValue) => {
    // 디바이스별 속성값 먼저 확인
    if (devicePropertyTab && 
        selectedComponent.deviceProperty?.[devicePropertyTab]?.[property] !== undefined) {
      return selectedComponent.deviceProperty[devicePropertyTab][property];
    }
    
    // 최상위 속성 확인
    if (selectedComponent[property] !== undefined) {
      return selectedComponent[property];
    }
    
    return defaultValue;
  };

  return (
    <>
      <SkinSelector 
        componentType={selectedComponent?.type}
        currentSkin={getValue('skin', '')}
        onSkinChange={(skin) => handleComponentPropsChange('skin', skin)}
      />
      
      <div className="property-group">
        <div className="property-group-title">
          <i className="fas fa-magic"></i> Parallax 효과 설정
        </div>
        <div className="property-control">
          <label>배경 이동 속도</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input 
              type="range" 
              min="0" 
              max="1" 
              step="0.05" 
              value={getValue('parallaxSpeed', 0.3)} 
              onChange={(e) => handleComponentPropsChange('parallaxSpeed', parseFloat(e.target.value))}
              style={{ flex: 1 }}
            />
            <span>{(getValue('parallaxSpeed', 0.3)).toFixed(2)}</span>
          </div>
          <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
            값이 클수록 배경 이미지가 더 많이 움직입니다. 0으로 설정하면 배경이 고정됩니다.
          </small>
        </div>
        
        <div className="property-control">
          <label>배경 시작 위치</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input 
              type="range" 
              min="-20" 
              max="20" 
              step="5" 
              value={getValue('startPosition', 0)} 
              onChange={(e) => handleComponentPropsChange('startPosition', parseInt(e.target.value))}
              style={{ flex: 1 }}
            />
            <span>{getValue('startPosition', 0)}%</span>
          </div>
          <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
            스크롤 시작 시 배경 이미지 위치입니다. (음수: 위로 이동, 양수: 아래로 이동)
          </small>
        </div>
        
        <div className="property-control">
          <label>배경 끝 위치</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input 
              type="range" 
              min="60" 
              max="120" 
              step="5" 
              value={getValue('endPosition', 80)} 
              onChange={(e) => handleComponentPropsChange('endPosition', parseInt(e.target.value))}
              style={{ flex: 1 }}
            />
            <span>{getValue('endPosition', 80)}%</span>
          </div>
          <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
            스크롤 끝에서의 배경 이미지 위치입니다. (값이 클수록 더 많이 이동합니다)
          </small>
        </div>
      </div>
      
      {/* <PaddingControl />
      <BorderRadiusControl />
      <StyleControl /> */}
    </>
  );
};

export default ParallaxProperties; 